import React, { useState, useMemo, useEffect } from 'react';
import {
  Select,
  MenuItem,
  Box,
  Checkbox,
  ListItemText,
  TextField,
  ListSubheader,
  InputAdornment,
  InputLabel,
  FormControl,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useListAgencySkillsQuery } from '@/graphql';
import useAuth from '@/hooks/useAuth';
import { ListAgencySkillsQuery } from '@/types/graphql';
import { sortBy } from '@/util/array';
import theme from '@/styles/colors';
import { SelectChangeEvent } from '@mui/material';
import { useAppDispatch } from '@/hooks/store';
import { setDashboardSkillIds } from '@/store/slices/filtersSlice';
import { useAppSelector } from '@/hooks/store';
import { Typography } from '@mui/material';
import useAnalytics from '@/util/analytics';
import { GAEvent } from '@/constants/gaevents';
import { OptionItem } from './OptionsModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';

type Item = ListAgencySkillsQuery['agency']['skillCategories'][0]['skills'][0];

const styles = {
  inputLabel: {
    color: theme.themeDefault,
    fontSize: '.875rem',
    fontWeight: 600,
    '&.Mui-focused': {
      color: theme.themeDefault,
      fontWeight: 500,
    },
    transform: 'translate(14px, 10px) scale(1)',
    '&.MuiInputLabel-shrink': {
      color: '#262626',
      fontWeight: 500,
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    transition: 'transform 0.2s ease-in-out, color 0.2s ease-in-out',
  },
  menuPaper: {
    maxHeight: '544px',
  },
  checkbox: {
    borderRadius: '20px',
    '&.Mui-checked': {
      color: theme.themeDefault,
    },
  },
  menuItem: {
    fontSize: '.875rem',
    fontWeight: 600,
  },
};

const SkillsSelect = ({
  setBookMarked,
  showMobileModal,
  phoneOnly,
}: {
  setBookMarked: (arg: boolean) => void;
  showMobileModal: (
    title: string,
    items: OptionItem[],
    selectedItems: string[],
    showFilter: boolean,
    updateFilters: (selectedItem: string[]) => void,
    selectAll?: boolean,
  ) => void;
  phoneOnly: boolean;
}) => {
  const { currentAgency, currentAdmin } = useAuth();
  const { logEvent } = useAnalytics();
  const dispatch = useAppDispatch();
  const { data } = useListAgencySkillsQuery({
    variables: { agencyId: currentAgency!.id },
  });

  const items: Item[] = useMemo(() => {
    if (data) {
      const skills = data.agency.skillCategories.flatMap(
        (category) => category.skills,
      );
      return sortBy(skills, 'name');
    }
    return [];
  }, [data]);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [open, setOpen] = useState(false);

  const filteredSkills = useMemo(() => {
    return items.filter((skill) =>
      skill.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [items, searchTerm]);

  const skillIdsInStore = useAppSelector(
    (state) => state.filters.dashboardFilters?.skillIds,
  );
  useEffect(() => {
    setSelectedItems((skillIdsInStore as string[]) || []);
  }, [skillIdsInStore]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    if (value.includes('all')) {
      if (selectedItems.length === items.length) {
        setSelectedItems([]);
        dispatch(setDashboardSkillIds(undefined));
      } else {
        setSelectedItems(items.map((item) => item.id)); // Store skill IDs
        dispatch(setDashboardSkillIds(items.map((item) => item.id)));
      }
    } else {
      setSelectedItems(value);
      dispatch(setDashboardSkillIds(value.length === 0 ? undefined : value));
    }

    setBookMarked(false);
    logEvent(GAEvent.DashboardSkillsFilter, currentAdmin?.user.id);
  };

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) {
      return <span>Skills</span>;
    }
    const selectedNames = selected
      .map((id) => items.find((item) => item.id === id)?.name)
      .filter(Boolean); // Get names for selected IDs

    const totalLength = selectedNames.reduce(
      (acc, name) => acc + name.length,
      0,
    );
    let displayText = selectedNames[0];
    let additionalCount = selectedNames.length - 1;

    if (totalLength > 10) {
      displayText = `${selectedNames[0].substring(0, 7)}...`;
      additionalCount = selectedNames.length - 1;
    }

    return (
      <Box display="flex" alignItems="center">
        <span>{displayText}</span>
        {additionalCount > 0 && <span>+{additionalCount}</span>}
      </Box>
    );
  };

  return (
    <FormControl style={{ maxWidth: '150px', marginRight: '10px' }}>
      <InputLabel id="skills-multi-select-label" sx={styles.inputLabel}>
        Skills
      </InputLabel>
      <Select
        labelId="skills-multi-select-label"
        label="Skills"
        multiple
        value={selectedItems}
        onChange={handleChange}
        renderValue={renderValue}
        fullWidth
        style={{ borderRadius: '8px' }}
        sx={{
          minWidth: 90,
          height: 40,
          color: theme.themeDefault,
          fontSize: '.875rem',
          fontWeight: 600,
          '.MuiOutlinedInput-notchedOutline': {
            borderColor:
              selectedItems.length > 0 ? '#262626' : theme.themeDefault,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor:
              selectedItems.length > 0 ? '#262626' : theme.themeDefault,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor:
              selectedItems.length > 0 ? '#262626' : theme.themeDefault,
            borderWidth: '1px',
          },
        }}
        open={open}
        IconComponent={(props) => (
          <FontAwesomeIcon
            style={{ color: theme.themeDefault }}
            icon={open ? faAngleUp : faAngleDown}
            {...props}
          />
        )}
        onOpen={() => {
          phoneOnly
            ? showMobileModal(
                'Skills',
                items,
                selectedItems,
                true,
                (selected: string[]) => {
                  handleChange({
                    target: { value: selected } as SelectChangeEvent<string[]>,
                  });
                },
                true,
              )
            : setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
          setTimeout(() => {
            setSearchTerm('');
          }, 150);
        }}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            style: styles.menuPaper,
          },
        }}
      >
        <MenuItem
          disableRipple
          style={{ cursor: 'default', pointerEvents: 'none' }}
        >
          <Typography
            variant="h6"
            style={{
              fontSize: '.875rem',
              fontWeight: 'bold',
              color: '#000',
              opacity: 100,
              marginBottom: '5px',
            }}
          >
            Skills
          </Typography>
        </MenuItem>
        <ListSubheader>
          <TextField
            size="small"
            autoFocus
            placeholder="Type to search..."
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
          />
        </ListSubheader>
        <MenuItem value="all">
          <Checkbox
            checked={selectedItems.length === items.length}
            sx={styles.checkbox}
          />
          <ListItemText
            primary="Select All"
            primaryTypographyProps={styles.menuItem}
          />
        </MenuItem>
        {filteredSkills.map((skill) => (
          <MenuItem key={skill.id} value={skill.id}>
            <Checkbox
              checked={selectedItems.includes(skill.id)}
              sx={styles.checkbox}
            />
            <ListItemText
              primary={skill.name}
              primaryTypographyProps={styles.menuItem}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SkillsSelect;
